import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-action-bar>` says about itself.
 *
 * This piece ARRANGES actions; it does not JOIN them. It draws no control of its own — every button
 * in it is a Button the caller supplied, and the overflow trigger is an OverflowMenu the caller
 * supplied. What it contributes is the row: context on the left, the action cluster pushed to the
 * trailing edge, a consistent gap, and wrapping instead of a sideways scroll when the surface is
 * narrow. Which actions stay inline and which go behind the overflow is the surface's decision, not
 * this piece's — it has no measuring logic and will not move anything for you.
 *
 * ITS NAME FOLLOWS `aria-label` (CAOS-648). The toolbar's accessible name is whatever the host's
 * `aria-label` says, whenever it is set, changed or removed, and "Actions" when there is none. It was
 * once copied in the constructor only, so a bar built by script — `document.createElement`, then
 * `setAttribute`, the order a page assembled from stored metadata always uses — was named "Actions"
 * for the life of the element.
 */
const docs: ComponentDocs = {
  aliases: ['toolbar', 'button bar', 'action row', 'command bar', 'footer actions'],

  answers: [
    'put several actions in a row at the top of a page',
    'keep the primary action and the secondary ones together',
    'show how many rows are selected next to what can be done with them',
    'lay out Save and Cancel at the foot of a form',
    'stop a row of buttons overflowing sideways on a narrow screen',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'framed',
      attribute: 'framed',
      type: 'boolean',
      description:
        'Draw the bar as a surface of its own — border, radius, filled background and inset padding — ' +
        'for a bar docked above a table or under a form. Without it the bar is transparent and only ' +
        'aligns what it holds.',
      default: false,
    },
  ],

  // None. Every event a person can raise from this bar comes from the controls the caller put in
  // it — the buttons raise click, the overflow menu raises its own selection event, and both pass
  // straight through. The bar attaches no listeners of its own.
  events: [],

  state: [],

  slots: [
    { name: '', takes: 'The inline action controls — Buttons — pushed to the trailing edge of the row.' },
    {
      name: 'start',
      takes:
        'Context on the leading edge, in muted text: a title, "3 selected", a filter summary. ' +
        'The region collapses when nothing is in it.',
    },
    {
      name: 'overflow',
      takes:
        'An OverflowMenu, drawn after the inline actions, holding the ones that did not earn a place ' +
        'in the row.',
    },
  ],

  tokens: [
    { token: '--caos-space-3', controls: 'The gap between the start region and the action cluster.' },
    { token: '--caos-space-2', controls: 'The gap between adjacent action controls.' },
    { token: '--caos-color-surface', controls: 'The fill of the framed variant.' },
    { token: '--caos-color-border', controls: 'The border of the framed variant.' },
    { token: '--caos-radius', controls: 'The corner radius of the framed variant.' },
    { token: '--caos-color-text-muted', controls: 'The ink of whatever is put in the start region.' },
    { token: '--caos-font', controls: 'The typeface of the start region.' },
  ],

  parts: [
    { part: 'bar', is: 'the toolbar itself' },
    { part: 'start', is: 'the leading group, before the gap' },
    { part: 'actions', is: 'the trailing group of controls' },
  ],

  methods: [],

  placing:
    'A block-level row, so it takes the full width of the region it is placed in and pushes its ' +
    'actions to that region’s trailing edge — which means the width of the region is what decides ' +
    'where the buttons land. Above a table, under a form, or at the head of a record surface. It wraps ' +
    'rather than ever scrolling sideways, so on a narrow screen the cluster drops onto its own line ' +
    'instead of disappearing off the edge. Use the framed variant when it is docked against something ' +
    'and needs an edge of its own; leave it plain when it sits in open page space.',

  whenToUse:
    'A surface has more than one action and they belong together as a group — typically one primary ' +
    'and one or two secondary, with the rest behind an overflow.',

  insteadUse: [
    {
      name: 'Button',
      when:
        'There is only ONE action. A bar around a single button adds a row, a gap and a toolbar role ' +
        'for nothing. Note that this piece never replaces Button — it holds them.',
    },
    {
      name: 'OverflowMenu',
      when:
        'Every action is secondary and none of them earns space in the row. That one collapses the ' +
        'whole set behind a single trigger; this one lays a set out in the open. They are usually used ' +
        'together — the menu goes in this bar’s overflow slot.',
    },
    {
      name: 'PageHeader',
      when:
        'The actions belong to the page as a whole and sit beside its title. That one has an actions ' +
        'slot of its own, so a bar underneath it would repeat the row.',
    },
    {
      name: 'SegmentedFilter',
      when: 'The row changes WHAT IS SHOWN rather than doing something.',
    },
  ],

  accessibility:
    'The row is marked as a `role="toolbar"` and carries a name, so assistive technology announces a ' +
    'group of related controls rather than loose buttons, and the start and action regions disappear ' +
    'from the layout when empty. Left to the author: the controls themselves. A toolbar does not give ' +
    'its buttons names, does not manage focus between them — each button is its own Tab stop — and does ' +
    'not decide which is primary. Give the bar an `aria-label` saying what its actions act on; the ' +
    'toolbar follows it whenever it changes, and is named "Actions" without one.',

  examples: [
    {
      key: 'surface-actions',
      label: 'A primary action and its neighbours',
      summary:
        'The plain bar in open page space: context on the left, the action cluster pushed to the right ' +
        'with one primary among them.',
      placement: {
        component: 'caos-action-bar',
        children: [
          // The start region is styled for plain muted text, and a text node cannot carry a `slot`
          // attribute — so the context is put there as a Tag, which is the smallest real piece that
          // can be addressed to a named slot.
          { component: 'caos-tag', inputs: { slot: 'start' }, children: ['3 selected'] },
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Clone'] },
          { component: 'caos-button', inputs: { variant: 'primary' }, children: ['Submit'] },
        ],
      },
    },
    {
      key: 'framed',
      label: 'Docked and framed',
      summary:
        'The same row given an edge of its own — the variant for a bar that sits hard against a table ' +
        'or a form rather than floating in page space.',
      placement: {
        component: 'caos-action-bar',
        inputs: { framed: true },
        children: [
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Cancel'] },
          { component: 'caos-button', inputs: { variant: 'primary' }, children: ['Save record'] },
        ],
      },
    },
    {
      key: 'with-overflow',
      label: 'With the rest behind a trigger',
      summary:
        'Two actions in the open and four behind the overflow slot — the pairing this bar is shaped for, ' +
        'and the reason the slot is called overflow rather than more.',
      // 109px was measured for a three-item menu plus a 4px gap; this menu carries four items, so the
      // reservation is scaled from that rather than measured. The bar is block-width and the menu
      // right-aligns to its own trigger, so alignEnd is not needed — the trigger is already at the end.
      stage: { opensBelow: 150 },
      placement: {
        component: 'caos-action-bar',
        inputs: { framed: true },
        children: [
          { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Clone'] },
          { component: 'caos-button', inputs: { variant: 'primary' }, children: ['Submit'] },
          {
            component: 'caos-overflow-menu',
            inputs: {
              slot: 'overflow',
              label: 'More record actions',
              items: [
                { value: 'export', label: 'Export as PDF' },
                { value: 'share', label: 'Share with the account' },
                { value: 'revise', label: 'Start a revision' },
                { value: 'delete', label: 'Delete record', danger: true },
              ],
            },
          },
        ],
      },
    },
  ],
};

export default docs;
